import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { BackButton } from "@/components/BackButton";
import { api } from "@/lib/api";
import { Lock, Play, Loader2 } from "lucide-react";

const TIER_COLOR = { bronze: "#E08D3C", silver: "#C7D2DE", gold: "#F5C842" };

export default function Library() {
  const [games, setGames] = useState([]);
  const [userLevel, setUserLevel] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/games").then((r) => { setGames(r.data.games); setUserLevel(r.data.user_level); }).finally(() => setLoading(false));
    // Mark this player as online for the live counter.
    const ping = () => api.post("/presence/ping").catch(() => {});
    ping();
    const id = setInterval(ping, 30000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A14]">
      <Header />

      <div className="relative scanlines overflow-hidden border-b border-cyan-500/20">
        <div className="absolute inset-0 opacity-25" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1511512578047-dfb367046420?crop=entropy&cs=srgb&fm=jpg&w=1600&q=70')", backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-[#0A0A14]/70" />
        <div className="relative max-w-7xl mx-auto px-6 py-16">
          <BackButton className="mb-6" />
          <p className="font-arcade text-xl text-[#FF006E] mb-1">// GAME VAULT</p>
          <h1 className="font-head font-900 text-4xl sm:text-5xl text-slate-50">THE ARCADE</h1>
          <p className="text-slate-400 mt-3">{userLevel >= 3 ? "Gold unlocked — every game is available." : "Snake & Brick Buster are free to play. Unlock the Gold vault with BTC."}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {loading ? (
          <div className="flex items-center gap-3 text-slate-400 font-arcade text-xl"><Loader2 className="w-6 h-6 animate-spin" /> LOADING VAULT…</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="games-grid">
            {games.map((g) => {
              const tierColor = TIER_COLOR[g.required_tier];
              return (
                <div key={g.id} data-testid={`game-card-${g.id}`} className={`group relative bg-[#11111A] border border-cyan-500/20 overflow-hidden transition-transform duration-200 ${g.unlocked ? "hover:-translate-y-1 hover:-translate-x-1 hover:shadow-[6px_6px_0px_#00F0FF]" : ""}`}>
                  <div className="relative aspect-square overflow-hidden scanlines">
                    <img src={g.thumb} alt={g.title} className={`w-full h-full object-cover transition-transform duration-300 ${g.unlocked ? "group-hover:scale-105" : "grayscale brightness-50"}`} />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A14] via-transparent to-transparent" />
                    <span className="absolute top-2 left-2 font-arcade text-sm px-2 py-0.5" style={{ background: tierColor, color: "#0A0A14" }}>{g.required_tier.toUpperCase()}</span>
                    {!g.playable && <span className="absolute top-2 right-2 font-arcade text-sm px-2 py-0.5 bg-slate-800 text-slate-400">SOON</span>}
                    {!g.unlocked && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 z-20">
                        <Lock className="w-8 h-8 text-slate-300" />
                        <span className="font-arcade text-base text-slate-300">{g.playable ? "UPGRADE TO PLAY" : "COMING SOON"}</span>
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-arcade text-2xl text-slate-50 leading-none">{g.title}</h3>
                    <p className="text-xs text-slate-500 mt-1">{g.genre}</p>
                    {g.unlocked ? (
                      <Link to={`/play/${g.id}`} data-testid={`play-${g.id}`} className="mt-3 w-full inline-flex items-center justify-center gap-2 font-arcade text-lg py-2 bg-[#00F0FF] text-black border border-[#00F0FF] hover:shadow-[3px_3px_0px_#FF006E] hover:-translate-x-0.5 hover:-translate-y-0.5 transition-transform duration-200">
                        <Play className="w-4 h-4" /> PLAY
                      </Link>
                    ) : g.playable ? (
                      <Link to={`/pay/${g.required_tier}`} data-testid={`unlock-${g.id}`} className="mt-3 w-full inline-flex items-center justify-center gap-2 font-arcade text-lg py-2 border border-slate-700 text-slate-400 hover:border-[#F5C842] hover:text-[#F5C842] transition-colors duration-200">
                        <Lock className="w-4 h-4" /> UNLOCK
                      </Link>
                    ) : (
                      <div className="mt-3 w-full text-center font-arcade text-lg py-2 border border-slate-800 text-slate-600">SOON</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
